import React, { useState, useRef } from "react";
import Editor from "@monaco-editor/react";
import {
  FiDownload,
  FiRefreshCw,
  FiCopy,
  FiSun,
  FiMoon,
  FiPlay,
  FiCode,
  FiTerminal,
} from "react-icons/fi";
import { runCode } from "../../../ApiServices/ApiServices";
import { handleError } from "../../../Utils";

const languageOptions = [
  { id: 105, name: "C++ (GCC 14.1.0)", extension: "cpp" },
  { id: 103, name: "C (GCC 14.1.0)", extension: "c" },
  { id: 91, name: "Java (JDK 17.0.6)", extension: "java" },
  { id: 100, name: "Python (3.12.5)", extension: "py" },
  { id: 102, name: "JavaScript (Node.js 22.08.0)", extension: "js" },
  { id: 101, name: "TypeScript (5.6.2)", extension: "ts" },
  { id: 106, name: "Go (1.22.0)", extension: "go" },
  { id: 98, name: "PHP (8.3.11)", extension: "php" },
  { id: 83, name: "Swift (5.2.3)", extension: "swift" },
  { id: 108, name: "Rust (1.85.0)", extension: "rs" },
  { id: 111, name: "Kotlin (2.1.10)", extension: "kt" },
  { id: 99, name: "R (4.4.1)", extension: "r" },
];

const getDefaultCode = (languageId) => {
  switch (languageId) {
    case 105: // C++
      return `#include <iostream>
using namespace std;

int main() {
    cout << "Hello World!" << endl;
    return 0;
}`;

    case 103: // C
      return `#include <stdio.h>

int main() {
    printf("Hello World!\\n");
    return 0;
}`;

    case 91: // Java
      return `class Main {
    public static void main(String[] args) {
        System.out.println("Hello World!");
    }
}`;

    case 100: // Python
      return `print("Hello World!")`;

    case 102: // JavaScript
      return `console.log("Hello World!");`;

    case 101: // TypeScript
      return `console.log("Hello World!");`;

    case 106: // Go
      return `package main

import "fmt"

func main() {
    fmt.Println("Hello World!")
}`;

    case 98: // PHP
      return `<?php
echo "Hello World!";
?>`;

    case 83: // Swift
      return `import Swift

print("Hello World!")`;

    case 108: // Rust
      return `fn main() {
    println!("Hello World!");
}`;

    case 111: // Kotlin
      return `fun main() {
    println("Hello World!")
}`;

    case 99: // R
      return `print("Hello World!")`;

    default:
      return `// Write your code here`;
  }
};

const getEditorLanguage = (languageId) => {
  switch (languageId) {
    case 105:
    case 103:
      return "cpp";
    case 91:
      return "java";
    case 100:
      return "python";
    case 102:
    case 101:
      return "javascript";
    case 106:
      return "go";
    case 98:
      return "php";
    case 83:
      return "swift";
    case 108:
      return "rust";
    case 111:
      return "kotlin";
    case 99:
      return "r";
    default:
      return "javascript";
  }
};

const OnlineCompiler = () => {
  const [languageId, setLanguageId] = useState(105);
  const [code, setCode] = useState(getDefaultCode(105));
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [executionStats, setExecutionStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const editorRef = useRef(null);

  const handleLanguageChange = (newLanguageId) => {
    setLanguageId(newLanguageId);
    setCode(getDefaultCode(newLanguageId));
    setOutput("");
    setExecutionStats(null);
  };

  const handleRunCode = async () => {
    setLoading(true);
    setOutput("Running...");
    setExecutionStats(null);

    try {
      const data = {};
      data.code = code;
      data.input = input;
      data.languageId = languageId;
      const response = await runCode(data);
      const result = response?.data;

      setOutput(
        result.stdout || result.stderr || result.compile_output || "No output"
      );

      // Set execution statistics
      if (result) {
        setExecutionStats({
          memory: result.memory ? `${result.memory} KB` : "N/A",
          time: result.time
            ? `${(parseFloat(result.time) * 1000).toFixed(0)} ms`
            : "N/A",
          status: result.status?.description || "Unknown",
        });
      }
    } catch (error) {
      return handleError(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor;
  };

  const handleReset = () => {
    setCode(getDefaultCode(languageId));
    setOutput("");
    setExecutionStats(null);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
  };

  const handleDownload = () => {
    const selectedLang = languageOptions.find((lang) => lang.id === languageId);
    const extension = selectedLang?.extension || "txt";
    const blob = new Blob([code], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `code.${extension}`;
    link.href = url;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div
      className={`min-h-screen transition-all duration-300 ${
        isDarkMode
          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
          : "bg-gradient-to-br from-blue-50 via-white to-purple-50"
      }`}
    >
      <div className="mt-16 px-4 py-8 max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div
          className={`rounded-2xl p-6 mb-8 backdrop-blur-sm border transition-all duration-300 ${
            isDarkMode
              ? "bg-gray-800/50 border-gray-700/50 shadow-2xl shadow-blue-500/10"
              : "bg-white/70 border-gray-200/50 shadow-2xl shadow-purple-500/10"
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`p-3 rounded-xl ${
                  isDarkMode
                    ? "bg-blue-600/20 text-blue-400"
                    : "bg-blue-100 text-blue-600"
                }`}
              >
                <FiCode size={24} />
              </div>
              <h1
                className={`text-3xl font-bold bg-gradient-to-r ${
                  isDarkMode
                    ? "from-blue-400 to-purple-400"
                    : "from-blue-600 to-purple-600"
                } bg-clip-text text-transparent`}
              >
                Online Compiler
              </h1>
            </div>

            {/* Theme toggle - only visible on desktop */}
            <button
              onClick={toggleTheme}
              className={`hidden lg:block p-3 rounded-xl transition-all duration-300 hover:scale-110 ${
                isDarkMode
                  ? "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                  : "bg-gray-800/10 text-gray-700 hover:bg-gray-800/20"
              }`}
              title={
                isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"
              }
            >
              {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
            </button>
          </div>

          {/* Language Selector + Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-wrap w-full lg:w-auto">
              <label
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Choose Language:
              </label>
              <select
                className={`px-4 py-2 rounded-xl border-2 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 w-[100%] sm:w-auto ${
                  isDarkMode
                    ? "bg-gray-700/50 border-gray-600 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50"
                }`}
                value={languageId}
                onChange={(e) => handleLanguageChange(Number(e.target.value))}
              >
                {languageOptions.map((lang) => (
                  <option key={lang?.id} value={lang?.id}>
                    {lang?.name}
                  </option>
                ))}
              </select>
              
              {/* Theme toggle for mobile - placed after language selector */}
              <button
                onClick={toggleTheme}
                className={`lg:hidden p-3 rounded-xl transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                    : "bg-gray-800/10 text-gray-700 hover:bg-gray-800/20"
                }`}
                title={
                  isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"
                }
              >
                {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
              </button>
            </div>

            {/* Toolbar Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                title="Download Code"
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? "bg-blue-600/20 text-blue-400 hover:bg-blue-600/30"
                    : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                }`}
              >
                <FiDownload size={18} />
              </button>
              <button
                onClick={handleReset}
                title="Reset Code"
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? "bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30"
                    : "bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                }`}
              >
                <FiRefreshCw size={18} />
              </button>
              <button
                onClick={handleCopy}
                title="Copy Code"
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? "bg-green-600/20 text-green-400 hover:bg-green-600/30"
                    : "bg-green-100 text-green-600 hover:bg-green-200"
                }`}
              >
                <FiCopy size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
          {/* Code Editor Section */}
          <div
            className={`rounded-2xl overflow-hidden backdrop-blur-sm border transition-all duration-300 ${
              isDarkMode
                ? "bg-gray-800/50 border-gray-700/50 shadow-2xl shadow-blue-500/10"
                : "bg-white/70 border-gray-200/50 shadow-2xl shadow-purple-500/10"
            }`}
          >
            <div
              className={`px-4 lg:px-6 py-3 lg:py-4 border-b flex items-center gap-3 ${
                isDarkMode
                  ? "border-gray-700/50 bg-gray-800/30"
                  : "border-gray-200/50 bg-gray-50/50"
              }`}
            >
              <FiCode
                className={isDarkMode ? "text-blue-400" : "text-blue-600"}
                size={20}
              />
              <span
                className={`font-semibold text-sm lg:text-base ${
                  isDarkMode ? "text-gray-200" : "text-gray-800"
                }`}
              >
                Code Editor
              </span>
            </div>

            <div className="p-2 lg:p-4">
              <div className="rounded-xl overflow-hidden border-2 border-gray-600/30">
                <Editor
                  height="350px" 
                  className="lg:!h-[500px]" 
                  language={getEditorLanguage(languageId)}
                  value={code}
                  onChange={(value) => setCode(value || "")}
                  onMount={handleEditorDidMount}
                  theme={isDarkMode ? "vs-dark" : "light"}
                  options={{
                    fontSize: window.innerWidth < 768 ? 12 : 14, 
                    fontFamily: "JetBrains Mono, Fira Code, monospace",
                    minimap: { enabled: window.innerWidth >= 1024 }, 
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    lineNumbers: window.innerWidth >= 768 ? "on" : "off", 
                    renderLineHighlight: "all",
                    selectOnLineNumbers: true,
                    roundedSelection: false,
                    readOnly: false,
                    cursorStyle: "line",
                    automaticLayout: true,
                    scrollbar: {
                      vertical: "auto",
                      horizontal: "auto",
                      verticalScrollbarSize: window.innerWidth < 768 ? 8 : 12,
                      horizontalScrollbarSize: window.innerWidth < 768 ? 8 : 12,
                    },
                  }}
                />
              </div>

              {/* Execution Stats + Run Button */}
              <div className="mt-4 lg:mt-6 flex items-center justify-between flex-wrap gap-4">
                {/* Execution Statistics */}
                <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
                  {executionStats && (
                    <>
                      {/* Status Badge */}
                      <div
                        className={`px-3 lg:px-4 py-1 lg:py-2 rounded-lg lg:rounded-xl text-xs lg:text-sm font-semibold transition-all duration-300 ${
                          executionStats?.status === "Accepted"
                            ? isDarkMode
                              ? "bg-green-500/20 text-green-400 border border-green-500/30"
                              : "bg-green-100 text-green-800 border border-green-300"
                            : isDarkMode
                            ? "bg-red-500/20 text-red-400 border border-red-500/30"
                            : "bg-red-100 text-red-800 border border-red-300"
                        }`}
                      >
                        {executionStats?.status}
                      </div>
                      {/* Time and Memory Stats */}
                      <div className="flex items-center gap-2 lg:gap-4">
                        <div
                          className={`px-2 lg:px-3 py-1 lg:py-2 rounded-md lg:rounded-lg text-xs lg:text-sm font-medium ${
                            isDarkMode
                              ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                              : "bg-blue-50 text-blue-700 border border-blue-200"
                          }`}
                        >
                          <span className="opacity-75">Time: </span>
                          <span className="font-semibold">
                            {executionStats.time}
                          </span>
                        </div>
                        <div
                          className={`px-2 lg:px-3 py-1 lg:py-2 rounded-md lg:rounded-lg text-xs lg:text-sm font-medium ${
                            isDarkMode
                              ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                              : "bg-purple-50 text-purple-700 border border-purple-200"
                          }`}
                        >
                          <span className="opacity-75">Memory: </span>
                          <span className="font-semibold">
                            {executionStats.memory}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Run Button */}
                <button
                  onClick={handleRunCode}
                  disabled={loading}
                  className={`w-full lg:w-auto flex items-center justify-center gap-2 lg:gap-3 px-4 lg:px-6 py-2 lg:py-3 rounded-lg lg:rounded-xl text-sm lg:text-base font-semibold transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 ${
                    loading
                      ? isDarkMode
                        ? "bg-gray-600/50 text-gray-400 cursor-not-allowed"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : isDarkMode
                      ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/25"
                      : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/25"
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 lg:h-4 lg:w-4 border-2 border-white border-t-transparent"></div>
                      Running...
                    </>
                  ) : (
                    <>
                      <FiPlay size={14} className="lg:w-4 lg:h-4" />
                      Run Code
                    </>
                  )}
                </button>
              </div>

            </div>
          </div>

          {/* Input/Output Panel */}
          <div className="flex flex-col gap-4 lg:gap-6">
            {/* Input Section */}
            <div
              className={`rounded-2xl overflow-hidden backdrop-blur-sm border transition-all duration-300 ${
                isDarkMode
                  ? "bg-gray-800/50 border-gray-700/50 shadow-2xl shadow-blue-500/10"
                  : "bg-white/70 border-gray-200/50 shadow-2xl shadow-purple-500/10"
              }`}
            >
              <div
                className={`px-4 lg:px-6 py-3 lg:py-4 border-b flex items-center gap-3 ${
                  isDarkMode
                    ? "border-gray-700/50 bg-gray-800/30"
                    : "border-gray-200/50 bg-gray-50/50"
                }`}
              >
                <FiTerminal
                  className={isDarkMode ? "text-green-400" : "text-green-600"}
                  size={20}
                />
                <span
                  className={`font-semibold text-sm lg:text-base ${
                    isDarkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  Input (stdin):
                </span>
              </div>
              <div className="p-2 lg:p-4">
                <textarea
                  rows="4" // Reduced rows for mobile
                  className={`w-full p-3 lg:p-4 rounded-xl border-2 transition-all duration-300 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 resize-none font-mono text-xs lg:text-sm lg:rows-8 ${
                    isDarkMode
                      ? "bg-gray-900/50 border-gray-600 text-gray-200 placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Enter input here..."
                />
              </div>
            </div>

            {/* Output Section */}
            <div
              className={`flex-1 rounded-2xl overflow-hidden backdrop-blur-sm border transition-all duration-300 ${
                isDarkMode
                  ? "bg-gray-800/50 border-gray-700/50 shadow-2xl shadow-blue-500/10"
                  : "bg-white/70 border-gray-200/50 shadow-2xl shadow-purple-500/10"
              }`}
            >
              <div
                className={`px-4 lg:px-6 py-3 lg:py-4 border-b flex items-center gap-3 ${
                  isDarkMode
                    ? "border-gray-700/50 bg-gray-800/30"
                    : "border-gray-200/50 bg-gray-50/50"
                }`}
              >
                <FiTerminal
                  className={isDarkMode ? "text-blue-400" : "text-blue-600"}
                  size={20}
                />
                <span
                  className={`font-semibold text-sm lg:text-base ${
                    isDarkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  Output:
                </span>
              </div>
              <div className="p-2 lg:p-4">
                <pre
                  className={`w-full p-3 lg:p-4 rounded-xl border-2 min-h-[150px] lg:min-h-[200px] max-h-[200px] lg:max-h-[300px] overflow-auto whitespace-pre-wrap font-mono text-xs lg:text-sm transition-all duration-300 ${
                    isDarkMode
                      ? "bg-gray-900/50 border-gray-600 text-gray-200"
                      : "bg-gray-50 border-gray-300 text-gray-900"
                  } ${output === "Running..." ? "animate-pulse" : ""}`}
                >
                  {output || (
                    <span
                      className={`italic ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Output will appear here...
                    </span>
                  )}
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`mt-8 text-center p-4 rounded-xl ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        ></div>
      </div>
    </div>
  );
};

export default OnlineCompiler;
